import { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  MapPin,
  MessageCircle,
  Monitor,
  Settings2,
  User,
  Users,
} from "lucide-react";
import { isVirtualEvent, type UpcomingEvent } from "@/api/eventDiscovery";
import { teamPaymentStatus } from "@/api/payments";
import { PaymentStatusPill } from "@/components/profile/PaymentStatusPill";
import { Button } from "@/components/ui/button";
import { EventDateTime } from "@/components/events/EventDateTime";
import { PayDialog } from "@/components/profile/PayDialog";
import { safeHref } from "@/lib/utils";
import RegistrationPanel from "@/components/profile/RegistrationDialog";
import { CancelRegistrationLink } from "@/components/profile/RegistrationDialog/CancelRegistrationLink";
import { RegistrationErrorBoundary } from "@/components/profile/RegistrationErrorBoundary";
import { CalendarDropdown } from "./CalendarDropdown";
import { useRegistrationIdentity } from "./useRegistrationIdentity";

/** Single-line event identity: location icon + city/country + date/time. */
function EventLine({
  event,
  size = "md",
}: {
  event: UpcomingEvent;
  size?: "sm" | "md";
}) {
  const virtual = isVirtualEvent(event);
  const Icon = virtual ? Monitor : MapPin;
  const iconClass = size === "sm" ? "h-3.5 w-3.5 shrink-0" : "h-3.5 w-3.5";
  const dateClass = size === "sm" ? "text-xs" : "text-muted-foreground";
  return (
    <>
      <span className="inline-flex items-center gap-1 font-medium text-foreground">
        <Icon className={iconClass} />
        {event.city}
        {event.country ? `, ${event.country}` : ""}
      </span>
      <span className={dateClass}>
        <EventDateTime
          start={event.date_start}
          end={event.date_finish}
          tz={event.timezone}
          variant={virtual ? "date-range" : "datetime"}
          layout="inline"
          hideYourTime
        />
      </span>
    </>
  );
}

function MeetingPoint({
  event,
  className,
}: {
  event: UpcomingEvent;
  className: string;
}) {
  return (
    <div
      className={`${className} flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-muted-foreground`}
    >
      {event.location_name && (
        <span>
          Meeting point:{" "}
          {safeHref(event.location_link) ? (
            <a
              href={safeHref(event.location_link)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2"
            >
              {event.location_name}
            </a>
          ) : (
            event.location_name
          )}
        </span>
      )}
      {event.location_name && safeHref(event.route_link) && (
        <span aria-hidden="true">·</span>
      )}
      {safeHref(event.route_link) && (
        <a
          href={safeHref(event.route_link)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2"
        >
          Route
        </a>
      )}
    </div>
  );
}

export function RegisteredCard({
  event,
  personId,
  active,
  onManage,
  onChanged,
  onClose,
  quizrunLabel,
}: {
  event: UpcomingEvent;
  personId: number;
  active: boolean;
  onManage: (e: UpcomingEvent) => void;
  onChanged: () => void;
  onClose: () => void;
  /** Optional "Quizrun #N · Month Year" label shown at the top of the card when
   *  rendered without an outer collapsible header (registered-only profile view). */
  quizrunLabel?: string;
}) {
  const [identityKey, setIdentityKey] = useState(0);
  const identity = useRegistrationIdentity(
    event.event_id,
    personId,
    identityKey,
  );
  const [payOpen, setPayOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  // The caller's own participation + payment come from the same identity fetch
  // (their row in members) — no separate payment round-trip needed.
  const participationId = identity?.participationId ?? null;
  const myMember = identity?.members.find((m) => m.person_id === personId);
  const myStatus = myMember?.payment_status ?? null;
  const isFree = myMember?.is_free ?? false;

  // Payment status is DB-driven. `free` means a genuine first-timer/comped spot.
  // payment_status is now only 'paid' or null (unpaid = no data).
  const effectiveStatus: import("@/api/payments").TeamPaymentStatus = isFree
    ? "free"
    : (myStatus ?? "unpaid");
  const isPaid = myStatus === "paid";

  const isTeam = identity?.category === "team";

  // The action button reflects what the REGISTRATION still owes, not just the
  // caller's own row — a captain who has already paid must still be able to
  // settle their team-mates.
  const iOwe = !isPaid && !isFree;
  // Paying for someone else relies on the dialog's member checklist, which only
  // exists on fixed-price events. A pay-what-you-want charge carries no member
  // selection, so it would bill the payer a second time and leave the member
  // they meant to cover still unpaid — offer support there instead.
  const canPayOthers =
    (event.price_cents ?? 0) > 0 &&
    (identity?.members ?? []).some(
      (m) =>
        m.person_id !== personId && !m.is_free && m.payment_status !== "paid",
    );
  const showPay = iOwe || canPayOthers;

  // Header pill = TEAM aggregate for teams, the caller's own status for solo.
  const headerStatus = isTeam
    ? teamPaymentStatus(identity?.members ?? [])
    : effectiveStatus;

  const virtual = isVirtualEvent(event);
  const showMeetingPoint =
    !virtual && (event.location_name || event.route_link);
  const chatHref = !virtual ? safeHref(event.chat_link) : undefined;

  const handleChanged = () => {
    setIdentityKey((k) => k + 1);
    onChanged();
  };

  return (
    <div className="relative rounded-lg border bg-card p-4 shadow-sm">
      {quizrunLabel && (
        <div className="mb-2 text-xs text-muted-foreground">{quizrunLabel}</div>
      )}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        {isTeam ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
            <Users className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Team · {identity.teamName || "(unnamed)"}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
            <User className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Solo
          </span>
        )}
        <PaymentStatusPill status={headerStatus} />
      </div>

      <div className="mt-2 flex flex-col gap-0.5 text-sm">
        <EventLine event={event} />
      </div>

      {showMeetingPoint && <MeetingPoint event={event} className="mt-0.5" />}

      {/* Group chat — only surfaced to registered participants (this card). */}
      {chatHref && (
        <a
          href={chatHref}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-medium text-primary underline underline-offset-2"
        >
          <MessageCircle className="h-3.5 w-3.5 shrink-0" />
          Join the group chat
        </a>
      )}

      {/* Cash note — shown whenever a Pay action is offered, including to an
         already-paid captain who still has team-mates to settle. */}
      {showPay && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          Pay online, or pay your host in cash on the day.
          {isTeam && " Each player's payment is tracked individually."}
        </p>
      )}
      {/* First-timer note: only a genuine DB `free`/comped first event reaches
          here now — so the celebratory "on us" copy is accurate. */}
      {!isPaid && isFree && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          Your first regular run is on us 🎉 — make an optional donation if
          you'd like to support the project.
        </p>
      )}

      {!showPay && !isFree && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {isTeam ? "Everyone's covered 🎉" : "You're all paid 🎉"} — tips are
          optional and go straight into running the next quiz.
        </p>
      )}

      {/* Action buttons — stacked on mobile, 50/50 from sm up. One button, two
          meanings: settle what's outstanding, or (once nothing is) tip. Keeping
          them as separate actions is what stops a tip being recorded as someone's
          ticket. */}
      <div className="mt-3">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {participationId &&
            (showPay ? (
              <Button
                size="sm"
                className="w-full"
                onClick={() => setPayOpen(true)}
              >
                {iOwe ? "Pay Now" : "Pay for your team"}
              </Button>
            ) : (
              <Button
                size="sm"
                className="w-full"
                onClick={() => setSupportOpen(true)}
              >
                Support Project
              </Button>
            ))}
          <CalendarDropdown event={event} className="w-full" />
        </div>

        {/* Manage button — teams only (member list to manage). Solo has no list,
            so its two actions (Switch to team / Cancel) render inline below
            instead of behind a Manage toggle. */}
        {isTeam && (
          <Button
            variant="outline"
            size="sm"
            className="mt-2 w-full"
            onClick={() => onManage(event)}
            aria-expanded={active}
            aria-label="Manage team"
          >
            <Settings2 className="h-4 w-4 mr-2" />
            Manage team
            {active ? (
              <ChevronUp className="h-4 w-4 ml-auto" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-auto" />
            )}
          </Button>
        )}

        {/* Cancelling shouldn't require opening the manage panel first. When
            expanded, the panel's own footer link takes over. */}
        {isTeam && !active && identity?.canEdit && (
          <div className="mt-2 flex justify-end">
            <CancelRegistrationLink
              eventId={event.event_id}
              isTeam
              onCancelled={() => {
                handleChanged();
                onClose();
              }}
            />
          </div>
        )}
      </div>

      {/* Inline manage panel: always shown for solo (its Switch-to-team / Cancel
          actions ARE the controls); toggled by the Manage button for teams. */}
      {(isTeam ? active : true) && (
        <RegistrationErrorBoundary>
          <RegistrationPanel
            event={event}
            personId={personId}
            onChanged={handleChanged}
            onClose={onClose}
            autoScroll={isTeam}
          />
        </RegistrationErrorBoundary>
      )}
      {participationId && (
        <>
          <PayDialog
            open={payOpen}
            onOpenChange={setPayOpen}
            mode="pay"
            eventId={event.event_id}
            participationId={participationId}
            priceCents={event.price_cents ?? null}
            priceCurrency={event.price_currency ?? null}
            members={identity?.members}
            onPaid={handleChanged}
          />
          <PayDialog
            open={supportOpen}
            onOpenChange={setSupportOpen}
            mode="support"
            eventId={event.event_id}
            participationId={participationId}
            priceCents={event.price_cents ?? null}
            priceCurrency={event.price_currency ?? null}
          />
        </>
      )}
    </div>
  );
}

export function RegisterRow({
  event,
  active,
  personId,
  onRegister,
  onChanged,
  onClose,
}: {
  event: UpcomingEvent;
  active: boolean;
  personId: number;
  onRegister: (e: UpcomingEvent) => void;
  onChanged: () => void;
  onClose: () => void;
}) {
  const virtual = isVirtualEvent(event);
  const showMeetingPoint =
    !virtual && (event.location_name || safeHref(event.route_link));

  return (
    <li className="rounded-lg border bg-card p-3 shadow-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm">
          <EventLine event={event} />
        </div>
        {/* While the form is open, Register/Cancel live together in the form
            footer below — don't duplicate a toggle button up here. */}
        {!active && (
          <Button size="sm" onClick={() => onRegister(event)}>
            Register
          </Button>
        )}
      </div>
      {showMeetingPoint && <MeetingPoint event={event} className="mt-1" />}
      {active && (
        <RegistrationErrorBoundary>
          <RegistrationPanel
            event={event}
            personId={personId}
            onChanged={onChanged}
            onClose={onClose}
          />
        </RegistrationErrorBoundary>
      )}
    </li>
  );
}
